import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import { useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import cn from 'classnames'
import Script from 'next/script'
import { memo } from 'react'
// Components
import Loader from '@/components/newLoader/Loader'
import BasicHeadSeo from '@/components/basicHeadSeo'
import Header from '@/components/newHeader/Header'
import Questions from '@/components/blog/Questions'
import CarouselPuppies from '@/components/puppy-travel/carouselPuppies'
import Footer from '@/components/newFooter/newFooter'
import { comments } from '@/components/reviews/customer-opinions'
import Trustpilot from '@/components/trustpilot'

import { ROBOTS_META } from 'const'
// Styles
import styles from './index.module.scss'
// Images
import mainImage from '/public/reviews/image-header.png'
import mainImageMobile from '/public/reviews/image-header-mobile.png'

interface CardTypes {
  title: string
  text: string
  name: string
  date: string
  logo?: string
  logoGoogle?: string
  stars: string
}

const Card = memo(({ elem }: { elem: CardTypes }) => {
  const [showContent, setShowContent] = useState(false)

  const {
    stars,
    title,
    text: content,
    name,
    date,
    logo: icon,
    logoGoogle: iconGoogle,
  } = elem

  return (
    <div className={styles.cardContainer}>
      <img className={styles.stars} src={stars} alt="icon five starts" />

      <h4>{title}</h4>
      <p className={cn({ [styles.allText]: showContent })}>{content}</p>

      <div
        className={styles.btnView}
        onClick={() => setShowContent(!showContent)}
      >
        {showContent ? 'View Less' : 'View More'}
      </div>

      <p className={styles.name}>
        {name} <span>{date}</span>
      </p>

      {icon || iconGoogle ? (
        <img
          className={cn({
            [styles.icon]: icon,
            [styles.iconGoogle]: iconGoogle,
          })}
          src={icon || iconGoogle}
          alt="icon"
        />
      ) : null}
    </div>
  )
})

export default function Reviews() {
  const router = useRouter()
  const ref = useRef(null)

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  return (
    <>
      <BasicHeadSeo
        title="Customer Reviews | Mawoo Pets"
        description="Discover what real customers have to say about Mawoo Pets! See all reviews and shop with confidence!"
        url={`${process.env.NEXT_PUBLIC_ROOT_URL}/reviews`}
        image={`${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`}
        content={ROBOTS_META}
      />
      <Script
        type="text/javascript"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        async
      />
      <main className={styles.main}>
        {router.isFallback && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />

        <div className={styles.content}>
          <section className={styles.header}>
            <picture className={styles.imageContainer}>
              <span className={styles.imageDesktop}>
                <Image
                  src={mainImage}
                  layout="intrinsic"
                  alt="article image"
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={`/_next/image?url=${mainImage}&w=16&q=1`}
                />
                <img
                  src="/reviews/heart-icon.svg"
                  className={styles.heart1}
                  alt="heart icon"
                />
                <img
                  src="/reviews/heart-icon-2.svg"
                  className={styles.heart2}
                  alt="heart icon"
                />
                <img
                  src="/reviews/heart-icon.svg"
                  className={styles.heart3}
                  alt="heart icon"
                />
              </span>

              <span className={styles.imageMobile}>
                <Image
                  src={mainImageMobile}
                  layout="responsive"
                  width={1074}
                  height={500}
                  quality={100}
                  objectFit="fill"
                  alt="article image"
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={`/_next/image?url=${mainImageMobile}&w=16&q=1`}
                />
              </span>
            </picture>

            <div className={styles.infoContainer}>
              <h1 className={styles.title}>
                Read real reviews left by <br />
                our customers!
              </h1>
              <p className={styles.text}>
                Our flight nannies delivering pawfection to every state! We love
                seeing our puppies find their forever homes and get lots of love
                and snuggles! <br />
                <br />
                Tag your photo on instagram with <b>#mawoopets</b> for a chance
                to be featured!
              </p>

              <Trustpilot />
            </div>
          </section>

          <section className={styles.carousel}>
            <h2 className={styles.title}>
              Our adorable pups with their furever families!
            </h2>
          </section>

          <div className={styles.containerCarousel}>
            <CarouselPuppies />
          </div>
          <section className={styles.customerReviews}>
            <h1 className={styles.title}>Don't take our word for it!</h1>
            <h1 className={styles.subtitle}>
              See what our customer have to say!
            </h1>

            <div className={styles.cardReview}>
              {comments?.map((elem) => (
                <Card key={elem.id} elem={elem} />
              ))}
            </div>

            <div className={styles.swiper}>
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperPrev]: !isBeginning,
                  [styles.disabledPrev]: isBeginning,
                })}
                onClick={goPrev}
              />
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperNext]: !isEnd,
                  [styles.disabledNext]: isEnd,
                })}
                onClick={goNext}
              />
              <Swiper
                speed={1000}
                initialSlide={0}
                grabCursor
                pagination={{ clickable: true }}
                breakpoints={{
                  1020: {
                    slidesPerView: 3,
                    spaceBetween: 10,
                  },
                  860: {
                    slidesPerView: 2,
                    spaceBetween: -40,
                  },
                  540: {
                    slidesPerView: 2,
                    spaceBetween: 10,
                  },
                  360: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                  },
                }}
                ref={ref}
              >
                {comments &&
                  comments?.map((elem, index) => (
                    <SwiperSlide key={index}>
                      <div className={styles.cardSwipe}>
                        <Card key={index} elem={elem} />
                      </div>
                    </SwiperSlide>
                  ))}
              </Swiper>
            </div>
          </section>

          <div className={styles.line} />
          <Questions />
        </div>
        <Footer />
      </main>
    </>
  )
}
